/**
 * The Briefs page's decisions that are not words (routes/twin/brief.tsx): which tab and period the URL asks for, a
 * period's neighbours, the tabs' keys, the periods below grouped, the names a brief's ids read as, the sidebar's
 * new-brief mark, and which gap an empty or failed list is. Pure.
 */
import type {
  BriefCadence,
  BriefListItem,
  CadenceBrief,
  CadenceChildItem,
  CadenceMover,
  PlantBrief,
} from "@/lib/api/brief";
import { isPlantDay } from "@/lib/plant-days";
import type { PlantTime } from "@/lib/shift-calendar";
import { parseShiftKey, parseWeekKey } from "./cadence-words";
import { briefGapOf, type BriefGap } from "./state";

/** A tab of the Briefs page: a cadence, or the overall brief. */
export type BriefsTab = BriefCadence | "overall";

export const BRIEFS_TABS: readonly { id: BriefsTab; label: string }[] = [
  { id: "shift", label: "Shift" },
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
  { id: "overall", label: "Overall" },
];

/** The tab `?cadence=` asks for; null when it asks for none the page has. */
export function briefsTabOf(raw: string | null): BriefsTab | null {
  return BRIEFS_TABS.find((t) => t.id === raw)?.id ?? null;
}

/**
 * The tab the page opens on when the URL names none: Day when the plant has a day brief, else Overall. Day while its
 * list is read (it decides), and on the sample plant; Overall when the list fails or is not served.
 */
export function defaultBriefsTab(s: { live: boolean; dayItems: BriefListItem[] | undefined; failed: boolean }): BriefsTab {
  if (!s.live) return "day";
  if (s.failed) return "overall";
  return s.dayItems === undefined || s.dayItems.length > 0 ? "day" : "overall";
}

/** The tab a key moves to in the tab list (the WAI-ARIA tabs pattern); null for any other key. */
export function tabAfterKey(tab: BriefsTab, key: string): BriefsTab | null {
  const i = BRIEFS_TABS.findIndex((t) => t.id === tab);
  const n = BRIEFS_TABS.length;
  if (key === "ArrowRight") return BRIEFS_TABS[(i + 1) % n].id;
  if (key === "ArrowLeft") return BRIEFS_TABS[(i - 1 + n) % n].id;
  if (key === "Home") return BRIEFS_TABS[0].id;
  if (key === "End") return BRIEFS_TABS[n - 1].id;
  return null;
}

/** The page's query: `cadence=day&period=2026-08-18`. The overall brief has no period. */
export function briefsSearch(tab: BriefsTab, period?: string | null): string {
  const q = new URLSearchParams({ cadence: tab });
  if (tab !== "overall" && period) q.set("period", period);
  return q.toString();
}

/** Whether a key names a period of the cadence on the plant clock ("2026-08-18.1", "2026-08-18", "2026-W34", ...). */
export function isPeriodKey(cadence: BriefCadence, key: string): boolean {
  switch (cadence) {
    case "shift":
      return parseShiftKey(key) !== null;
    case "day":
      return isPlantDay(key);
    case "week":
      return parseWeekKey(key) !== null;
    case "month":
      return /^\d{4}-(0[1-9]|1[0-2])$/.test(key);
    case "year":
      return /^\d{4}$/.test(key);
  }
}

/** How many of the newest periods set the share a landing period must cast (landingPeriod). */
const LANDING_LOOKBACK = 28;
/** The share of the largest of them a period must cast to open the page: a tenth. */
const LANDING_SHARE = 0.1;

/**
 * The period the page opens on when none is asked for: the newest that cast at least a tenth of the most any of the
 * newest 28 cast, so the page does not open on a day that poured 2 pieces while the plant cast 30,000 a day (the
 * 2026-09-28 page walk, P120). A period whose castings are not known (a shift, an older server) counts as cast. The
 * newest when none qualifies.
 */
export function landingPeriod(items: BriefListItem[]): BriefListItem | null {
  if (items.length === 0) return null;
  const most = Math.max(0, ...items.slice(0, LANDING_LOOKBACK).map((it) => it.cast ?? 0));
  const fair = items.find((it) => it.cast === undefined || it.cast === null || it.cast >= most * LANDING_SHARE);
  return fair ?? items[0];
}

/**
 * The period to show: the one `?period=` asks for (a period of the cadence, listed or not: the brief route answers
 * for any), else the newest in the list that cast a fair share (landingPeriod), else none yet.
 */
export function periodFor(cadence: BriefCadence, asked: string | null, items: BriefListItem[] | undefined): string | null {
  if (asked !== null && isPeriodKey(cadence, asked)) return asked;
  return landingPeriod(items ?? [])?.period_key ?? null;
}

/** A period's neighbours in a list held newest first. A period the list does not hold has none. */
export function neighbours(
  items: BriefListItem[],
  key: string | null,
): { older: BriefListItem | null; newer: BriefListItem | null } {
  const i = key === null ? -1 : items.findIndex((it) => it.period_key === key);
  if (i < 0) return { older: null, newer: null };
  return { older: items[i + 1] ?? null, newer: items[i - 1] ?? null };
}

/** The notable items of one period below. */
export interface ChildGroup {
  cadence: BriefCadence;
  period_key: string;
  items: CadenceChildItem[];
}

/**
 * The periods below, each with its items, in period order (the 2026-10-24 sweep, item 6.3). The brief lists its movers
 * above their limits, then those below, then its excursions, each in period order, so a group's first item can come
 * late: a day, a month or a year sorts by its key; a day's shifts, whose labels need not sort as their times, keep the
 * order the brief's three lists agree on (shiftOrder).
 */
export function childGroups(children: CadenceChildItem[]): ChildGroup[] {
  const groups: ChildGroup[] = [];
  for (const c of children) {
    const g = groups.find((x) => x.cadence === c.cadence && x.period_key === c.period_key);
    if (g) g.items.push(c);
    else groups.push({ cadence: c.cadence, period_key: c.period_key, items: [c] });
  }
  if (groups.every((g) => g.cadence !== "shift")) {
    return groups.sort((a, b) => a.period_key.localeCompare(b.period_key));
  }
  const order = shiftOrder(children);
  return groups.sort((a, b) => order.indexOf(a.period_key) - order.indexOf(b.period_key));
}

/**
 * The shifts in the order the brief's three lists (movers above, movers below, excursions) agree on: each list is in
 * shift order, so a shift a list names before another comes first; where no list orders two shifts, the one listed
 * first does. Pure.
 */
function shiftOrder(children: readonly CadenceChildItem[]): string[] {
  const phase = (c: CadenceChildItem) => (c.kind === "excursion" ? 2 : c.signal === "above" ? 0 : 1);
  const keys: string[] = [];
  for (const c of children) if (!keys.includes(c.period_key)) keys.push(c.period_key);
  const before = new Map<string, Set<string>>(keys.map((k) => [k, new Set<string>()]));
  for (const p of [0, 1, 2]) {
    const list: string[] = [];
    for (const c of children) if (phase(c) === p && !list.includes(c.period_key)) list.push(c.period_key);
    for (let i = 1; i < list.length; i++) before.get(list[i])!.add(list[i - 1]);
  }
  const out: string[] = [];
  while (out.length < keys.length) {
    // The first key, by first appearance, with every key a list puts before it placed; a cycle (lists that disagree)
    // places the first left.
    const next = keys.find((k) => !out.includes(k) && [...before.get(k)!].every((b) => out.includes(b))) ??
      keys.find((k) => !out.includes(k))!;
    out.push(next);
  }
  return out;
}

/**
 * What a notable mover of a period below is (the item does not say: the 2026-10-24 sweep, item 6.3): a defect where
 * the brief or the overall brief knows the word as one, a line where the period's lines name it, else a part, said
 * only where the overall brief is read, so a defect it leaves out is never called a part. Undefined for an excursion,
 * or where it cannot be told.
 */
export function childMoverKind(
  c: CadenceChildItem,
  brief: Pick<CadenceBrief, "scrap" | "movers">,
  overall?: Pick<PlantBrief, "scrap" | "defects">,
): CadenceMover["kind"] | undefined {
  if (c.kind !== "mover") return undefined;
  // A word the brief books outside the foundry's own process (scrap.outside) is no part either: a child brief stored
  // before cadence/v18 can still list one as a mover.
  const outside = (s: { outside?: { words: readonly string[] } } | null | undefined) => s?.outside?.words.includes(c.label) ?? false;
  if (outside(brief.scrap) || outside(overall?.scrap)) return "defect";
  const defects = new Set<string>();
  for (const r of brief.scrap?.pareto ?? []) defects.add(r.defect);
  for (const m of brief.movers) if (m.kind === "defect") defects.add(m.key);
  for (const r of overall?.scrap.pareto ?? []) defects.add(r.defect);
  for (const d of overall?.defects ?? []) defects.add(d.defect);
  if (defects.has(c.key)) return "defect";
  if ((brief.scrap?.lines ?? []).some((l) => l.line === c.key) || brief.movers.some((m) => m.kind === "line" && m.key === c.key)) {
    return "line";
  }
  if (brief.movers.some((m) => m.kind === "part" && m.key === c.key)) return "part";
  return overall ? "part" : undefined;
}

/** A defect id in the plant's own word: the brief's labels, then the overall brief's, else the id in words. */
export function defectLabelsOf(brief: CadenceBrief, overall?: PlantBrief): (id: string) => string {
  const labels = new Map<string, string>();
  for (const p of overall?.scrap.pareto ?? []) labels.set(p.defect, p.label);
  for (const d of overall?.defects ?? []) labels.set(d.defect, d.label);
  for (const p of brief.scrap?.pareto ?? []) labels.set(p.defect, p.label);
  for (const m of brief.movers) if (m.kind === "defect") labels.set(m.key, m.label);
  for (const r of brief.risks) labels.set(r.defect, r.label);
  return (id) => labels.get(id) ?? id.replace(/_/g, " ");
}

/** The playbook mechanisms' titles: the brief's movers carry theirs; the overall brief carries every one it tested. */
export function mechanismTitlesOf(brief: CadenceBrief, overall?: PlantBrief): Map<string, string> {
  const titles = new Map<string, string>();
  for (const d of overall?.defects ?? []) for (const m of d.mechanisms) titles.set(m.id, m.title);
  for (const mv of brief.movers) for (const m of mv.mechanisms) titles.set(m.id, m.title);
  return titles;
}

/**
 * The sidebar's mark on "Plant brief": the newest day or shift brief was written after the viewer's last visit to
 * the page. `seen` is that visit (null: never visited; undefined: storage cannot be read, so no mark).
 */
export function briefsMarkVisible(s: { newest: (string | null | undefined)[]; seen: string | null | undefined }): boolean {
  if (s.seen === undefined) return false;
  const written = s.newest.map((t) => (t ? Date.parse(t) : NaN)).filter((t) => !Number.isNaN(t));
  if (written.length === 0) return false;
  const seen = s.seen === null ? NaN : Date.parse(s.seen);
  return Number.isNaN(seen) || Math.max(...written) > seen;
}

/**
 * Why a cadence tab has nothing to show: a gap of the read, no brief at this cadence yet, or (shifts) none ever, as the
 * plant has no shift calendar to cut its days by.
 */
export type CadenceGap = BriefGap | "empty" | "no_calendar";

/**
 * Why a cadence's list is empty: a shift tab on a plant whose plant time names no shifts gets none ever (the platform
 * never guesses a shift); anything else, not read or no plant time at all (the sample), has none yet.
 */
export function emptyGapOf(cadence: BriefCadence, pt: PlantTime | null | undefined): "empty" | "no_calendar" {
  return cadence === "shift" && pt != null && (pt.shift_calendar ?? []).length === 0 ? "no_calendar" : "empty";
}

/** Which gap a failed list read is. The engine's own 404 says there is no brief yet. */
export function listGapOf(err: unknown): CadenceGap {
  const gap = briefGapOf(err);
  return gap === "no_run" ? "empty" : gap;
}
